import type { FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import type { FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import type { BarLayout, BarPillContext } from '@/app/common/filters/surfaces/bar/resultsPills/barPills.ts';
import { assembleBarPills } from '@/app/common/filters/surfaces/bar/resultsPills/barPills.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { Language } from '@/domainLogic/index.ts';
import type { FilterOrder } from '@/app/common/filters/session/filterOrder.ts';
import { EMPTY_FILTER_ORDER } from '@/app/common/filters/session/filterOrder.ts';
import { getSmartQuickFilters } from '@/app/common/filters/quickFilters/personaQuickFilters.ts';
import type { FilterFlags } from '@/app/common/filters/core/filterFlags.ts';
import { resolveQuickFilters } from '@/app/common/filters/quickFilters/resolveQuickFilters.ts';
import { createQuickFilterPill } from '@/app/common/filters/variants/evo/pills/QuickFilterPill.tsx';
import { orderEvoFilterBarPills } from '@/app/common/filters/variants/evo/pills/orderEvoFilterBarPills.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';

type CreateEvoPillsProps = BarPillContext & {
  lang: Language;
  searchParams: SearchParams;
  filterOrder?: FilterOrder;
  config?: FilterFlags;
};

// The order every persona row of the design draws the group pills in; a pill it lacks goes last.
const EVO_BAR_ORDER: FiltersGroupId[] = [
  CUSTOM_GROUP_NAME.ALL_FILTERS,
  FILTER_NAME.SORT,
  FILTER_NAME.PRICE_RANGE,
  FILTER_NAME.MEAL_PLAN,
  FILTER_NAME.PROPERTY_TYPE,
  FILTER_NAME.STARS,
  FILTER_NAME.RATING,
  CUSTOM_GROUP_NAME.FACILITIES,
  FILTER_NAME.DESTINATION_AIRPORT,
  FILTER_NAME.OVERNIGHT_FLIGHTS,
];

const barRank = (pill: FilterPill) => {
  const rank = EVO_BAR_ORDER.indexOf(pill.id);
  return rank === -1 ? EVO_BAR_ORDER.length : rank;
};

const compactOnMobile = (pill: FilterPill, layout: BarLayout): FilterPill =>
  layout === 'mobile' && pill.id === CUSTOM_GROUP_NAME.ALL_FILTERS ? { ...pill, iconOnly: true } : pill;

const createPills = (
  { searchParams, filterOrder = EMPTY_FILTER_ORDER, lang, config, ...context }: CreateEvoPillsProps,
  layout: BarLayout
): FilterPill[] => {
  const barPills = assembleBarPills(context, layout, { groupIcons: true }).sort((a, b) => barRank(a) - barRank(b));
  const groupPills = barPills.map((pill) => {
    const selectionOrder = filterOrder.options[pill.id as string];
    return compactOnMobile(selectionOrder ? { ...pill, selectionOrder } : pill, layout);
  });

  const groupPillIds = new Set(groupPills.map((pill) => pill.id as string));
  const quickFilterPills = resolveQuickFilters({
    filtersGroups: context.filtersGroups,
    t: context.t,
    lang,
    quickFilters: getSmartQuickFilters({ config, searchParams, productsData: context.productsData }),
  })
    .filter((resolved) => resolved.available)
    .map(createQuickFilterPill)
    .filter((pill) => groupPillIds.has(pill.id as string));

  return orderEvoFilterBarPills({ pills: groupPills, quickFilterPills, groupOrder: filterOrder.groups });
};

export const createEvoBarPills = (props: CreateEvoPillsProps): FilterPill[] => createPills(props, 'desktop');

export const createEvoMobileBarPills = (props: CreateEvoPillsProps): FilterPill[] => createPills(props, 'mobile');
